// @flow

import type {
  OrganizerSpecial,
  ParsedHotelDescription,
  ParsedJourney,
} from 'components/TravelDetailsOverlay/models';
import type {CancellationRule, Flight, Journey} from 'redux/v1/journey/models';
import type {PaymentType} from 'redux/v1/paymentTravel/models';

import {flatten} from 'lodash';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';

import {
  EXPEDIA_TYPE_CANCELLATION_RULE_NOT_REFUNDABLE,
  EXPEDIA_TYPE_CANCELLATION_RULE_PARTIAL_REFUNDABLE,
  EXPEDIA_TYPE_CANCELLATION_RULE_REFUNDABLE,
} from 'consts';
import useHotelImage from 'hooks/useHotelImage';
import {useParsedMultiRooms} from 'hooks/useParseMultiRooms';
import i18next from 'i18n';
import {getTourOperatorDescription} from 'redux/tourOperator/reducer';
import {getOrganizerCancellation} from 'redux/v1/organizerCancellation/reducer';
import {
  PAYMENT_TYPE_CC,
  PAYMENT_TYPE_INSTANTPAYMENT,
  PAYMENT_TYPE_INVOICE,
  PAYMENT_TYPE_PAYPAL,
  PAYMENT_TYPE_SEPA,
} from 'redux/v1/paymentTravel/models';
import {getPaymentTypes} from 'redux/v1/paymentTravel/paymentTypes/reducer';
import {
  capitalizeFirst,
  formatISOtoDateTimeString,
  parseDateStringWithoutTz,
  priceFormatter,
} from 'utils';

const paymentsMapping = {
  [PAYMENT_TYPE_PAYPAL]: 'PAYPALLIGHT',
  [PAYMENT_TYPE_INSTANTPAYMENT]: 'INSTANTBANKTRANSFERLIGHT',
  VI: 'VISA',
  DC: 'DINERS_CLUB',
};

const paymentPositions = {
  [PAYMENT_TYPE_CC]: 1,
  [PAYMENT_TYPE_SEPA]: 2,
  [PAYMENT_TYPE_INVOICE]: 3,
  [PAYMENT_TYPE_INSTANTPAYMENT]: 4,
  [PAYMENT_TYPE_PAYPAL]: 5,
};

const parsePayments = (payments: {...PaymentType, position: number}[]) => {
  const availablePayments = payments.filter((payment) => payment.available);
  for (const payment of availablePayments) {
    if (
      payment.code === PAYMENT_TYPE_CC &&
      payment.cards &&
      payment.cards.length > 0
    ) {
      payment.cards = payment.cards.filter((card) => card.available);
    }
  }
  const sortedPayments = availablePayments.sort(
    (a, b) => a.position - b.position
  );

  return flatten(
    sortedPayments.map((payment) => {
      return payment.code === PAYMENT_TYPE_CC &&
        payment.cards &&
        payment.cards.length > 0 ? payment.cards.map(
          (card) => paymentsMapping[card.code] || card.code
        ) : paymentsMapping[(payment.code: string)] || payment.code;
    })
  );
};

const parseFlights = (flights: Flight[] | null) => {
  if (flights && flights.length > 0) {
    return flights.map((flight) => {
      return {
        ...flight,
        departureDateTime: parseDateStringWithoutTz(flight.departureDateTime),
        arrivalDateTime: parseDateStringWithoutTz(flight.arrivalDateTime),
        direction: flight.outward ? 'outbound' : 'inbound',
        stayTime: flight.stopoverDuration || 0,
      };
    });
  }

  return null;
};

const parseCancellationRules = (rules: CancellationRule[] | null) => {
  if (rules === undefined) {
    return [];
  }

  return rules?.reduce<Array<OrganizerSpecial>>((accumulator, rule) => {
    if (rule.ruleType === EXPEDIA_TYPE_CANCELLATION_RULE_NOT_REFUNDABLE) {
      accumulator.push({
        type: 'none',
        text: i18next.t('travelOverlay:cancellationNotice'),
        hint: i18next.t('travelOverlay:cancellationRules', {
          context: 'noRefundable',
        }),
        inviaType: rule.ruleType,
      });
    } else if (
      rule.ruleType === EXPEDIA_TYPE_CANCELLATION_RULE_PARTIAL_REFUNDABLE ||
      rule.ruleType === EXPEDIA_TYPE_CANCELLATION_RULE_REFUNDABLE
    ) {
      accumulator.push({
        type: 'none',
        text: i18next.t('travelOverlay:cancellationNotice'),
        hint: i18next.t('travelOverlay:cancellationRules', {
          fee: priceFormatter('de-DE', 'EUR').format(rule.amount / 100),
          dateTime: rule.validTo ? formatISOtoDateTimeString(rule.validTo) : '',
          context: 'refundable',
        }),
        inviaType: rule.ruleType,
      });
    }

    return accumulator;
  }, []);
};

export const useParseJourney = (journey: Journey): ParsedJourney => {
  const {t} = useTranslation('travelDetailsOverlay');
  const {promoImage, hotel} = useHotelImage();
  const payments = useSelector(getPaymentTypes).map((payment) => ({
    ...payment,
    position: paymentPositions[payment.code],
  }));
  const cancellation = useSelector(getOrganizerCancellation);
  const tourOperatorDescription = useSelector(getTourOperatorDescription);

  const multiRooms = useParsedMultiRooms(journey.rooms);

  const curatedDescription: ParsedHotelDescription[] =
    journey.hotel.description.map((desc) => {
      if (desc.description) {
        return {
          title: desc.title,
          content: desc.description,
        };
      } else if (desc.content) {
        return {
          title: desc.title,
          content: desc.content,
        };
      }

      return {
        title: desc.title,
        content: '',
      };
    });

  const primaryBoardType = journey.cateringType?.split('#')[0] ?? '';
  const boardTypeDescriptions = {
    'IT06-NB': 'travelOverlay:boardTypeDescription.noBoard',
    'IT06-BB': 'travelOverlay:boardTypeDescription.breakfast',
    'IT06-HB': 'travelOverlay:boardTypeDescription.halfBoard',
    'IT06-FB': 'travelOverlay:boardTypeDescription.fullBoard',
    'IT06-AI': 'travelOverlay:boardTypeDescription.allInclusive',
  };

  const cancellationInfo = useMemo(
    () => parseCancellationRules(journey?.cancellationRules),
    [journey?.cancellationRules]
  );

  const parsedJourney = {
    travelType: journey.port === 653 ? 'ownArrival' : 'travelPackage',
    supplierAgbLink: journey.supplier.agbPdfLink || journey.supplier.agbLink,
    services: {
      duration: journey.stays || journey.duration,
      overnights: journey.overnights,
      departureDate: journey.departureDate,
      returnDate: journey.returnDate,
      room: capitalizeFirst(journey.room) || multiRooms.parsedRooms[0].roomType,
      roomDescription: journey.roomDescription,
      multiRooms: multiRooms || {},
      catering: capitalizeFirst(journey.catering),
      cateringCode: journey.cateringType,
      cateringDescription:
        boardTypeDescriptions[primaryBoardType] &&
        t(boardTypeDescriptions[primaryBoardType]),
      includedServices: journey.includedServices,
      oceanViewInfo: journey.oceanViewInfo,
      baggageIncluded: journey.baggageIncluded,
      flightsLuggage: journey.flightsLuggage,
      flightsHandLuggage: journey.flightsHandLuggage,
      specialInfo: journey.supplier.specialInfo,
      cancellationInfo,
    },
    payments: parsePayments(payments),
    tourOperator: {
      id: journey.supplier.code,
      name: journey.supplier.name,
      logo: journey.organizerLogo,
    },
    organizerNote: journey.organizerNote.split('\n').join('<br>'),
    hotel: {
      ...hotel,
      image: promoImage,
      tourOperatorDescription,
      curatedDescription,
    },
    flightsLuggage: journey.flightsLuggage
      ? {weight: journey.flightsLuggage.weight}
      : null,
    flightsHandLuggage: journey.flightsHandLuggage
      ? {weight: journey.flightsHandLuggage.weight}
      : null,
    flights: parseFlights(journey.flights),
  };

  if (journey.booked) {
    parsedJourney.services.specialInfo = journey.supplier.specialInfo.filter(
      ({type}) =>
        type !== 'campaign' &&
        (cancellation?.included || type !== cancellation?.reference)
    );
  }

  return parsedJourney;
};
